<template>
  <ContentWrap>
    <el-alert
      class="mb-12px"
      type="info"
      :closable="false"
      show-icon
      title="企业/商会对平台已有资源发起「认领」，通过后该资源归属申请人（claimStatus=claimed、verified=true）；驳回需填写原因。"
    />
    <el-form :inline="true" :model="query" class="-mb-15px">
      <el-form-item label="审核状态">
        <el-select v-model="query.status" clearable class="!w-140px" placeholder="全部">
          <el-option label="待审核" value="pending" />
          <el-option label="已通过" value="approved" />
          <el-option label="已驳回" value="rejected" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">搜索</el-button>
        <el-button @click="reset">重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column prop="resourceName" label="认领资源" min-width="180" show-overflow-tooltip>
        <template #default="{ row }">{{ row.resourceName || '资源#' + row.resourceId }}</template>
      </el-table-column>
      <el-table-column prop="userId" label="申请人ID" width="130" />
      <el-table-column prop="claimNo" label="认领单号" width="170" show-overflow-tooltip />
      <el-table-column prop="role" label="认领人职务" width="130" show-overflow-tooltip />
      <el-table-column prop="mobile" label="手机号" width="130" />
      <el-table-column label="证明材料" min-width="200">
        <template #default="{ row }">
          <el-space wrap>
            <el-tag v-if="row.license" size="small" type="primary">营业执照</el-tag>
            <el-tag v-if="row.idProof" size="small">身份证明</el-tag>
            <el-tag v-if="row.authProof" size="small" type="warning">授权书</el-tag>
            <span v-if="!row.license && !row.idProof && !row.authProof" class="text-12px text-#909399">
              未上传
            </span>
          </el-space>
        </template>
      </el-table-column>
      <el-table-column label="审核状态" width="100">
        <template #default="{ row }">
          <el-tag :type="statusTag(row.status)" size="small">{{ statusText(row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="rejectReason" label="驳回原因" min-width="140" show-overflow-tooltip>
        <template #default="{ row }">{{ row.rejectReason || '-' }}</template>
      </el-table-column>
      <el-table-column label="审核时间" width="170">
        <template #default="{ row }">{{ formatTime(row.auditTime) }}</template>
      </el-table-column>
      <el-table-column label="申请时间" width="170">
        <template #default="{ row }">{{ formatTime(row.createTime) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="200" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openDetail(row)">详情</el-button>
          <template v-if="row.status === 'pending'">
            <el-button
              v-hasPermi="['bosscircle:resource:audit']"
              link
              type="success"
              @click="handleApprove(row)"
            >
              通过
            </el-button>
            <el-button
              v-hasPermi="['bosscircle:resource:audit']"
              link
              type="danger"
              @click="openReject(row)"
            >
              驳回
            </el-button>
          </template>
          <span v-else class="text-12px text-#909399">已处理</span>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="load"
    />
  </ContentWrap>

  <Dialog title="认领申请详情" v-model="detailVisible" width="680px">
    <el-descriptions v-if="current?.id" :column="2" border>
      <el-descriptions-item label="认领资源">
        {{ current.resourceName || '资源#' + current.resourceId }}
      </el-descriptions-item>
      <el-descriptions-item label="资源ID">{{ current.resourceId || '-' }}</el-descriptions-item>
      <el-descriptions-item label="认领单号">{{ current.claimNo || '-' }}</el-descriptions-item>
      <el-descriptions-item label="申请人ID">{{ current.userId ?? '-' }}</el-descriptions-item>
      <el-descriptions-item label="认领人职务">{{ current.role || '-' }}</el-descriptions-item>
      <el-descriptions-item label="手机号">{{ current.mobile || '-' }}</el-descriptions-item>
      <el-descriptions-item label="审核状态">
        <el-tag :type="statusTag(current.status)" size="small">
          {{ statusText(current.status) }}
        </el-tag>
      </el-descriptions-item>
      <el-descriptions-item label="驳回原因">{{
        current.rejectReason || '-'
      }}</el-descriptions-item>
      <el-descriptions-item label="申请时间">{{
        formatTime(current.createTime)
      }}</el-descriptions-item>
      <el-descriptions-item label="审核时间">{{
        formatTime(current.auditTime)
      }}</el-descriptions-item>
    </el-descriptions>
    <div class="mt-12px flex flex-wrap gap-16px">
      <div v-for="material in detailMaterials" :key="material.label" class="w-200px">
        <div class="mb-8px text-14px text-#909399">{{ material.label }}</div>
        <el-image
          v-if="isImageUrl(material.value)"
          :src="material.value"
          :preview-src-list="[material.value]"
          preview-teleported
          fit="contain"
          class="h-140px w-200px border border-solid border-[var(--el-border-color)]"
        />
        <div v-else-if="material.value" class="text-12px text-#909399">
          已提交（历史数据未存图片地址）
        </div>
        <div v-else class="text-12px text-#909399">未上传</div>
      </div>
    </div>
    <template #footer>
      <el-button @click="detailVisible = false">关 闭</el-button>
      <el-button
        v-if="current?.status === 'pending'"
        v-hasPermi="['bosscircle:resource:audit']"
        type="success"
        @click="handleApprove(current)"
      >
        通过
      </el-button>
    </template>
  </Dialog>

  <Dialog title="驳回认领申请" v-model="rejectVisible" width="500px">
    <el-form ref="rejectFormRef" :model="rejectForm" :rules="rejectRules" label-width="90px">
      <el-form-item label="资源">{{ current?.resourceName || '资源#' + current?.resourceId }}</el-form-item>
      <el-form-item label="驳回原因" prop="rejectReason">
        <el-input
          v-model="rejectForm.rejectReason"
          type="textarea"
          :rows="3"
          placeholder="请填写驳回原因，将展示给申请人"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="rejectVisible = false">取 消</el-button>
      <el-button :loading="rejecting" type="danger" @click="submitReject">确定驳回</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { formatDate } from '@/utils/formatTime'
import * as Api from '@/api/business/resource'
import type { FormInstance, FormRules } from 'element-plus'

defineOptions({ name: 'BossCircleResourceClaim' })

const route = useRoute()
const message = useMessage()

const loading = ref(false)
const list = ref<Api.ResourceClaimVO[]>([])
const total = ref(0)
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  status: (route.query.status as Api.ResourceAuditStatus) || undefined
})

const statusText = (s?: string) =>
  ({ pending: '待审核', approved: '已通过', rejected: '已驳回' } as Record<string, string>)[s ?? ''] || '-'
const statusTag = (s?: string) =>
  s === 'approved' ? 'success' : s === 'rejected' ? 'danger' : 'warning'

const formatTime = (value?: string | number) => (value ? formatDate(value) : '-')
/** 材料列的历史值是 'uploaded' 这类占位串，只有看着像地址的才当图片渲染，否则会出破图 */
const isImageUrl = (value?: string) =>
  !!value && /^(https?:\/\/|\/|data:image\/)/i.test(value.trim())

const load = async () => {
  loading.value = true
  try {
    const params: any = { pageNo: query.pageNo, pageSize: query.pageSize }
    if (query.status) params.status = query.status
    const data = await Api.getResourceClaimPage(params)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const search = () => {
  query.pageNo = 1
  load()
}

const reset = () => {
  query.status = undefined
  search()
}

const handleApprove = async (row: Api.ResourceClaimVO) => {
  await message.confirm(`确认通过「${row.resourceName || '该资源'}」的认领申请吗？`)
  await Api.auditResourceClaim({ id: row.id!, approved: true })
  message.success('已通过，资源归属申请人')
  detailVisible.value = false
  load()
}

const rejectVisible = ref(false)
const rejecting = ref(false)
const current = ref<Api.ResourceClaimVO>()
const rejectFormRef = ref<FormInstance>()
const rejectForm = reactive<{ rejectReason: string }>({ rejectReason: '' })
const rejectRules = {
  rejectReason: [{ required: true, message: '驳回原因不能为空', trigger: 'blur' }]
} as FormRules

const openReject = (row: Api.ResourceClaimVO) => {
  current.value = row
  rejectForm.rejectReason = ''
  rejectVisible.value = true
  nextTick(() => rejectFormRef.value?.clearValidate())
}

const submitReject = async () => {
  await rejectFormRef.value?.validate()
  rejecting.value = true
  try {
    await Api.auditResourceClaim({
      id: current.value!.id!,
      approved: false,
      rejectReason: rejectForm.rejectReason.trim()
    })
    message.success('已驳回')
    rejectVisible.value = false
    load()
  } finally {
    rejecting.value = false
  }
}

// ============ 详情：审核人要能看到三份证明材料本体 ============
const detailVisible = ref(false)
const MATERIAL_FIELDS: Array<{ key: 'license' | 'idProof' | 'authProof'; label: string }> = [
  { key: 'license', label: '营业执照' },
  { key: 'idProof', label: '身份证明' },
  { key: 'authProof', label: '授权书' }
]
const detailMaterials = computed(() =>
  MATERIAL_FIELDS.map((field) => ({
    label: field.label,
    value: current.value?.[field.key]
  }))
)

const openDetail = (row: Api.ResourceClaimVO) => {
  current.value = row
  detailVisible.value = true
}

onMounted(load)
</script>